import React, { useState, useEffect } from "react";
import { Form, Select, Checkbox, Space, Typography, Spin } from "antd";
import { useCustom } from "@refinedev/core";
import { useWatch } from "antd/es/form/Form";
import axAPI from "api/axios-instance";
import { SegmentType, TokenStatsResponse } from "../Lib/types";

const { Text } = Typography;

interface SegmentSelectorProps {
  form: any;
}

export const SegmentSelector: React.FC<SegmentSelectorProps> = ({ form }) => {
  const [searchValue, setSearchValue] = useState<string>("");
  const [filteredSegments, setFilteredSegments] = useState<SegmentType[]>([]);
  const [tokenStats, setTokenStats] = useState<TokenStatsResponse | null>(null);
  const [statsLoading, setStatsLoading] = useState<boolean>(false);

  const { data: segmentsData, isLoading } = useCustom({
    dataProviderName: "pushSegmentsDataProvider",
    url: "cms/push/segments",
    method: "get",
  });

  const segments = (segmentsData?.data || []) as SegmentType[];

  useEffect(() => {
    if (searchValue) {
      const filtered = segments.filter((segment: SegmentType) =>
        segment.name.toLowerCase().includes(searchValue.toLowerCase())
      );
      setFilteredSegments(filtered);
    } else {
      setFilteredSegments(segments);
    }
  }, [searchValue, segments]);

  const useSegments = form.getFieldValue("useSegments") as boolean;
  const segmentIds = (useWatch("segmentIds", form) as number[]) || [];

  // Стата токенов: сколько получателей реально получат пуш (fnl-12134)
  useEffect(() => {
    if (!useSegments) {
      setTokenStats(null);
      return;
    }
    let cancelled = false;
    setStatsLoading(true);
    axAPI
      .get<TokenStatsResponse>("/cms/push/segment/stats", {
        params: segmentIds.length ? { ids: segmentIds.join(",") } : {},
      })
      .then(({ data }) => {
        if (!cancelled) setTokenStats(data);
      })
      .catch(() => {
        if (!cancelled) setTokenStats(null);
      })
      .finally(() => {
        if (!cancelled) setStatsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [useSegments, segmentIds.join(",")]);

  // Обновляем значение useSegments при изменении
  const handleUseSegmentsChange = (e: { target: { checked: boolean } }) => {
    if (e.target.checked) {
      // Сбрасываем поля лиг и команд при выборе сегментов
      form.setFieldsValue({
        leagueId: null,
        groupId: null,
        teamId: null,
      });
    }
  };

  return (
    <Space direction="vertical" style={{ width: "100%" }}>
      <Form.Item name="useSegments" valuePropName="checked">
        <Checkbox onChange={handleUseSegmentsChange}>
          <Text strong>Отправить по сегментам</Text>
        </Checkbox>
      </Form.Item>

      {useSegments && (
        <>
          <Form.Item
            name="segmentIds"
            label="Выберите сегменты"
            rules={[
              {
                required: true,
                message: "Необходимо выбрать хотя бы один сегмент",
              },
            ]}
          >
            <Select
              mode="multiple"
              placeholder="Выберите сегменты"
              loading={isLoading}
              showSearch
              filterOption={false}
              onSearch={setSearchValue}
              notFoundContent={
                isLoading ? "Загрузка..." : "Сегменты не найдены"
              }
              style={{ width: "100%" }}
            >
              {filteredSegments.map((segment: SegmentType) => (
                <Select.Option key={segment.id} value={segment.id}>
                  {segment.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          {statsLoading ? (
            <Spin size="small" />
          ) : (
            tokenStats && (
              <Space direction="vertical" size={0}>
                {tokenStats.segment && (
                  <Text>
                    В выбранных сегментах: {tokenStats.segment.usersTotal}{" "}
                    пользователей, с токеном —{" "}
                    <Text strong>{tokenStats.segment.withToken}</Text>, без
                    токена — {tokenStats.segment.withoutToken}
                  </Text>
                )}
                {tokenStats.segment && (
                  <Text type="secondary">
                    Каналы: через AppMetrica —{" "}
                    <Text strong>{tokenStats.segment.viaAppmetrica ?? 0}</Text>{" "}
                    установок, через FCM — {tokenStats.segment.viaFcm ?? 0}
                  </Text>
                )}
                <Text type="secondary">
                  Всего в системе: {tokenStats.system.usersTotal} пользователей,
                  с актуальным токеном — {tokenStats.system.withToken}, без
                  токена — {tokenStats.system.withoutToken}; через AppMetrica —{" "}
                  {tokenStats.system.viaAppmetrica ?? 0} установок
                </Text>
              </Space>
            )
          )}
        </>
      )}
    </Space>
  );
};
